import { useEffect, useState } from 'preact/hooks';
import type { BuildListItem, BuildListResult, ServerClient } from './api.js';

export function buildStatusLabel(b: BuildListItem): string {
  switch (b.status) {
    case 'published': return 'Published version';
    case 'passed': return b.previewStatus ? 'Draft' : 'Ready to test';
    case 'failed': return 'Needs attention';
    case 'generating': return 'Creating';
    case 'gating': return 'Checking';
    case 'interrupted': return 'Stopped. Start again';
  }
}

export function MyApps({ api, onOpen, onNew }: {
  api: ServerClient; onOpen(id: string): void; onNew(): void;
}) {
  const [kind,setKind]=useState<''|'assignment'|'resource'>('');
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<BuildListResult | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setResult(null); setError(false);
    api.teacherBuilds(offset,kind||undefined).then(r => { if (active) setResult(r); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [api, offset, retry,kind]);
  return <section aria-labelledby="my-apps-title">
    <div class="workspace-heading">
      <div><h2 class="section-title" id="my-apps-title">My apps</h2>
        <p class="lede">Pick up where you left off. Each saved version stays here, including changes that need another try.</p></div>
      <button type="button" class="step" onClick={onNew}>Create an app</button>
    </div>
    <label class="publish-label" for="saved-kind">Show</label>
    <select id="saved-kind" class="field" value={kind} onChange={e=>{setKind(e.currentTarget.value as typeof kind);setOffset(0);}}>
      <option value="">All saved apps</option><option value="assignment">Assignments</option><option value="resource">Resources</option>
    </select>
    {error ? <div class="notice-line" role="alert">Your saved apps could not load.
      <button type="button" class="quiet" onClick={() => setRetry(n => n + 1)}>Try again</button>
    </div> : result === null ? <p role="status">Loading your apps…</p> : <>
      {result.builds.length === 0 ? <p class="workspace-empty">{offset === 0
        ? 'No saved apps yet. Create an app to start; TIP saves your request when you send it.'
        : 'No more saved apps on this page.'}</p> :
        <ol class="workspace-list" aria-label="Saved apps, newest first">
          {result.builds.map(b => <li key={b.id} class="workspace-row">
            <div class="workspace-copy">
              <span class={`workspace-status ${b.status === 'failed' || b.status === 'interrupted' ? 'state-warn' : ''}`}>{buildStatusLabel(b)}{b.kind==='resource'?' · Resource':''}{b.parentBuildId ? ' · Updated version' : ''}</span>
              <h3>{b.title || b.prompt.slice(0, 100) || 'Untitled app'}</h3>
              {b.title && b.prompt !== b.title ? <p>{b.prompt.length > 200 ? b.prompt.slice(0, 200) + '…' : b.prompt}</p> : null}
              <time dateTime={b.createdAt}>{new Date(b.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</time>
            </div>
            <button type="button" class="step" aria-label={`Open ${b.title || b.prompt.slice(0, 100) || 'untitled app'}`}
              onClick={() => onOpen(b.id)}>Open →</button>
          </li>)}
        </ol>}
      {offset > 0 || result.nextOffset != null ? <nav class="workspace-pages" aria-label="Saved apps pages">
        <button type="button" class="quiet" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>← Newer</button>
        <span>Page {Math.floor(offset / 25) + 1}</span>
        <button type="button" class="quiet" disabled={result.nextOffset == null} onClick={() => setOffset(result.nextOffset!)}>Older →</button>
      </nav> : null}
    </>}
  </section>;
}
